import cn from 'classnames'
import { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations'

import {
  PLANNING_SMB_TRANSLATION_KEYS,
  SMB_PERCENT_FRACTION_DIGITS,
  SMB_PERCENT_STEP,
  SMB_TOTAL_PERCENT,
} from '../../../../configs'
import { TSmbSplitResult } from '../../../../model/types'

import styles from './PlanningSmbCellStack.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS.modal

const toPercentText = (value: number) => value.toFixed(SMB_PERCENT_FRACTION_DIGITS)

export interface IPlanningSmbCellStackProps {
  /** Financial Plan amount behind this cell, already formatted */
  planLabel: ReactNode
  /** Financial Plan amount behind this cell, used to split into SMB and EU money */
  weight: number
  value: TSmbSplitResult
  baseline?: TSmbSplitResult
  baselineWeight?: number
  baselineLabel?: string
  /** An expanded row is calculated from what is below it and is never typed into */
  isEditable: boolean
  inputAriaLabel: string
  showFinancialData: boolean
  disabled?: boolean
  formatMoney: (value: number) => string
  onCommit: (smb: number) => void
}

/**
 * One cell of the split grid. Every cell - editable or calculated, quarter or year - is built
 * from the same stack, laid out in two sub columns, so every SMB figure and every EU figure
 * line up vertically all the way down the column.
 */
export const PlanningSmbCellStack = ({
  planLabel,
  weight,
  value,
  baseline,
  baselineWeight,
  baselineLabel,
  isEditable,
  inputAriaLabel,
  showFinancialData,
  disabled,
  formatMoney,
  onCommit,
}: IPlanningSmbCellStackProps) => {
  const committedText = value.isEmpty ? '' : toPercentText(value.smb)
  const [draft, setDraft] = useState(committedText)
  const isTouchedRef = useRef(false)

  // Follow the calculated value whenever it changes underneath us (an edit elsewhere in the
  // tree, a toggled branch), but never while the field is being typed into.
  useEffect(() => {
    setDraft(committedText)
    isTouchedRef.current = false
  }, [committedText])

  const draftSmb = Math.max(0, Math.min(SMB_TOTAL_PERCENT, Number(draft) || 0))
  const previewEu = draft === '' ? null : SMB_TOTAL_PERCENT - draftSmb

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    isTouchedRef.current = true
    setDraft(event.target.value)
  }

  const handleCommit = () => {
    if (!isTouchedRef.current || draft === '') {
      isTouchedRef.current = false
      setDraft(committedText)

      return
    }
    onCommit(draftSmb)
    // A destructive edit is committed only after confirmation. Keep the field showing the
    // last committed value until that decision is made; the value effect applies it afterwards.
    isTouchedRef.current = false
    setDraft(committedText)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur()
    }
  }

  const renderEntry = () => {
    if (isEditable) {
      return (
        <div className={styles.cols}>
          <div className={styles.col}>
            <span className={cn(styles.cap, styles.capSmb)}>SMB</span>
            <span className={styles.inputWrap}>
              <input
                aria-label={inputAriaLabel}
                className={styles.input}
                type="number"
                step={SMB_PERCENT_STEP}
                min={0}
                max={SMB_TOTAL_PERCENT}
                disabled={disabled}
                value={draft}
                onChange={handleChange}
                onBlur={handleCommit}
                onKeyDown={handleKeyDown}
              />
              {/* Part of the field, not of its value - the unit is never typed or edited */}
              <span aria-hidden className={styles.inputSuffix}>
                %
              </span>
            </span>
          </div>
          <div className={styles.col}>
            <span className={cn(styles.cap, styles.capEu)}>EU</span>
            <span className={styles.euValue}>
              {previewEu === null ? '–' : toPercentText(previewEu)}
              <span className={styles.euSuffix}>%</span>
            </span>
          </div>
        </div>
      )
    }

    if (value.isEmpty) {
      return (
        <span className={styles.empty}>
          <Translation span text={KEYS.notEntered} />
        </span>
      )
    }

    return (
      <div className={styles.cols}>
        <div className={styles.col}>
          <span className={cn(styles.cap, styles.capSmb)}>SMB</span>
          <span className={styles.computed}>
            <span className={styles.sigma}>Σ</span>
            {toPercentText(value.smb)}%
          </span>
        </div>
        <div className={styles.col}>
          <span className={cn(styles.cap, styles.capEu)}>EU</span>
          <span className={styles.computed}>{toPercentText(value.eu)}%</span>
        </div>
      </div>
    )
  }

  const renderSplitMoney = () => (
    <div className={styles.cols}>
      <div className={styles.col}>
        <span className={cn(styles.money, styles.moneySmb, { [styles.moneyEmpty]: value.isEmpty })}>
          {value.isEmpty ? '–' : formatMoney((weight * value.smb) / SMB_TOTAL_PERCENT)}
        </span>
      </div>
      <div className={styles.col}>
        <span className={cn(styles.money, styles.moneyEu, { [styles.moneyEmpty]: value.isEmpty })}>
          {value.isEmpty ? '–' : formatMoney((weight * value.eu) / SMB_TOTAL_PERCENT)}
        </span>
      </div>
    </div>
  )

  const renderBaseline = () => {
    if (!baseline || baseline.isEmpty) {
      return <span className={styles.empty}>-</span>
    }

    return (
      <div className={styles.cols}>
        <div className={styles.col}>
          <span className={styles.baselinePct}>{toPercentText(baseline.smb)}%</span>
          {showFinancialData && (
            <span className={styles.baselineMoney}>
              {formatMoney(((baselineWeight || 0) * baseline.smb) / SMB_TOTAL_PERCENT)}
            </span>
          )}
        </div>
        <div className={styles.col}>
          <span className={styles.baselinePct}>{toPercentText(baseline.eu)}%</span>
          {showFinancialData && (
            <span className={styles.baselineMoney}>
              {formatMoney(((baselineWeight || 0) * baseline.eu) / SMB_TOTAL_PERCENT)}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.stack}>
      {showFinancialData && <div className={cn(styles.row, styles.planRow)}>{planLabel}</div>}
      <div className={cn(styles.row, styles.entryRow)}>{renderEntry()}</div>
      {showFinancialData && <div className={styles.row}>{renderSplitMoney()}</div>}
      {baseline && baselineWeight !== undefined && baselineLabel && (
        <>
          <div className={cn(styles.row, styles.baselineHeader)}>
            <span className={styles.baselineTag}>
              {baselineLabel} <Translation span text={KEYS.baselineTag} />
            </span>
          </div>
          <div
            className={cn(styles.row, styles.baselineRow, {
              [styles.baselineEmpty]: baseline.isEmpty,
            })}
          >
            {renderBaseline()}
          </div>
        </>
      )}
    </div>
  )
}
